import { isToday } from 'date-fns';
import { useAtom, useAtomValue } from 'jotai';

import type { SelectedCountsFilter } from '../../../@types/appointments';
import type {
  Appointment,
  AppointmentProvider,
} from '../../../react-query/useAppointments';
import type { TaskCounts } from '../../../react-query/useGetTasks';
import { usePreferences } from '../../../react-query/usePreferences';
import type { TVisitSummary } from '../../../react-query/useVisitSummaries';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { selectDateAtom, selectedCountsFilterAtom } from '../atoms';
import { useRecommendationFilterCounts } from '../helpers';
import { OperatoriesDropdown } from '../operatoriesDropdown';
import { tasksOnAtom } from '../patientStrip/recommendedActions/atoms';
import { ProvidersDropdown } from '../providersDropdown';

import { DailyGoals } from './dailyGoals/DailyGoals';
import { RecommendationFilters } from './RecommendationFilters';
import { TasksFilters } from './TasksFilters';

export interface FiltersSidebarProps {
  appointments: Appointment[];
  isLoadingAppointments?: boolean;
  isLoadingVisitSummaries?: boolean;
  providersList: AppointmentProvider[];
  taskCounts: TaskCounts;
  visitSummaries?: TVisitSummary[];
}

export const FiltersSidebar = ({
  appointments,
  isLoadingAppointments = false,
  isLoadingVisitSummaries = false,
  providersList,
  taskCounts: gamifiedTaskCounts,
  visitSummaries,
}: FiltersSidebarProps) => {
  const [selectedCountsFilter, setSelectedCountsFilter] = useAtom(
    selectedCountsFilterAtom,
  );

  const { data: prefs } = usePreferences();
  const isHygieneViewEnabled = useAtomValue(tasksOnAtom);
  const isDailyGoalsEnabled = prefs?.enableDailyGoals === true;
  const selectedDate = useAtomValue(selectDateAtom);

  const { trackEvent } = useTrackEvent();
  const recommendationFilterCounts = useRecommendationFilterCounts(
    visitSummaries,
    appointments,
  );

  const {
    BW_DUE,
    FLUORIDE_DUE,
    PERIO_CHART_DUE,
    PERIO_MAINTENANCE_DUE,
    PROPHY_DUE,
    SCAN_OPPORTUNITY,
  } = gamifiedTaskCounts.taskCounts;

  const handleChangeFilter = (newFilter?: SelectedCountsFilter) => {
    const isSelected = newFilter === selectedCountsFilter;

    setSelectedCountsFilter(isSelected ? undefined : newFilter);
    trackEvent(mixpanelEvents.huddleTaskIndicatorFilterClick, {
      status: newFilter,
      bitewingsDueCount: BW_DUE.total - BW_DUE.complete,
      fluorideDueCount: FLUORIDE_DUE.total - FLUORIDE_DUE.complete,
      perioChartDueCount: PERIO_CHART_DUE.total - PERIO_CHART_DUE.complete,
      prophyDueCount: PROPHY_DUE.total - PROPHY_DUE.complete,
      perioMaintenanceDueCount:
        PERIO_MAINTENANCE_DUE.total - PERIO_MAINTENANCE_DUE.complete,
      scanOpportunityCount: SCAN_OPPORTUNITY.total - SCAN_OPPORTUNITY.complete,
      potentialCrownCount: recommendationFilterCounts.crown,
      potentialIncipientCount: recommendationFilterCounts.enamelRegen,
      possiblePerioCount: recommendationFilterCounts.perio,
      rootCanalCount: recommendationFilterCounts.rootCanal,
      fillingCount: recommendationFilterCounts.filling,
      extractionCount: recommendationFilterCounts.extraction,
      alignerCount: recommendationFilterCounts.aligner,
      implantCount: recommendationFilterCounts.implant,
      date: selectedDate,
      isToday: selectedDate ? isToday(selectedDate) : false,
    });
  };

  return (
    <div className="vai-w-[263px] vai-p-4 vai-bg-white vai-shadow-sm vai-flex vai-flex-col vai-gap-1 vai-overflow-y-scroll">
      <div className="vai-flex vai-flex-col vai-gap-4">
        <OperatoriesDropdown
          loading={isLoadingAppointments}
          source="PATIENT_LIST"
        />
        <ProvidersDropdown
          loading={isLoadingAppointments}
          providersList={providersList}
        />
      </div>

      {isDailyGoalsEnabled && <DailyGoals loading={isLoadingAppointments} />}

      {isHygieneViewEnabled && (
        <>
          <hr className="vai-h-[1px] vai-border-0 vai-bg-grey-200 vai-my-2" />

          <RecommendationFilters
            filterCounts={recommendationFilterCounts}
            loading={isLoadingAppointments || isLoadingVisitSummaries}
            onChangeFilter={handleChangeFilter}
          />

          <hr className="vai-h-[1px] vai-border-0 vai-bg-grey-200 vai-my-2" />

          <TasksFilters
            gamifiedTaskCounts={gamifiedTaskCounts}
            loading={isLoadingAppointments || isLoadingVisitSummaries}
            onChangeFilter={handleChangeFilter}
          />
        </>
      )}
    </div>
  );
};
